import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as authApi from '../api/auth';

export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: !!localStorage.getItem('access_token'),
      isLoading: false,
      error: null,

      signup: async ({ email, password, name }) => {
        set({ isLoading: true, error: null });
        // Clear any persisted learner data from a previous session before starting a new one
        const { useLearnerStore } = await import('./learnerStore');
        useLearnerStore.getState().clearLearners();
        try {
          const data = await authApi.signup({ email, password, name });

          // Email verification flow — no tokens yet, return pending state
          if (data.email_verification_pending) {
            set({ isLoading: false });
            return data;
          }

          if (!data.access_token) {
            set({ isLoading: false });
            throw new Error('Account created but sign-in failed. Please log in.');
          }
          set({ user: data, isAuthenticated: true, isLoading: false });

          // Refresh with full user profile (subscription, onboarding_complete, children)
          // signup response only returns minimal data — GET /users/me returns everything.
          try {
            const fullUser = await authApi.getMe();
            set({ user: fullUser });
            if (fullUser?.id) {
              useLearnerStore.getState().setUserId(fullUser.id);
            }
            // FP-LEARNER-STORE-RETIREMENT-01: useLearners() auto-fetches as
            // soon as isAuthenticated flips, so no imperative fetch here.
          } catch (profileErr) {
            console.error('Post-signup profile fetch failed:', profileErr.message || profileErr);
          }

          return data;
        } catch (err) {
          set({ error: err.message, isLoading: false });
          throw err;
        }
      },

      login: async ({ email, password }) => {
        set({ isLoading: true, error: null });
        // Clear any persisted learner data from a previous session before starting a new one
        const { useLearnerStore } = await import('./learnerStore');
        useLearnerStore.getState().clearLearners();
        try {
          const data = await authApi.login({ email, password });
          set({
            user: {
              ...data,
              id: data.user_id,  // normalize: login returns user_id, rest of app expects id
              onboarding_complete: data.onboarding_complete || false,
            },
            isAuthenticated: true,
            isLoading: false,
          });

          // Refresh with full user profile (onboarding_complete, is_admin, subscription, children)
          // The login response only returns id/email/name — GET /users/me returns everything.
          try {
            const fullUser = await authApi.getMe();
            set({ user: fullUser });

            if (fullUser?.id) {
              useLearnerStore.getState().setUserId(fullUser.id);
            }
            // FP-LEARNER-STORE-RETIREMENT-01: useLearners() auto-fetches as
            // soon as isAuthenticated flips, so no imperative fetch here.
          } catch (profileErr) {
            console.error('Post-login profile fetch failed:', profileErr.message || profileErr);
          }

          return data;
        } catch (err) {
          set({ error: err.message, isLoading: false });
          throw err;
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
        } finally {
          // Clear BOTH stores on logout
          set({ user: null, isAuthenticated: false });
          const { useLearnerStore } = await import('./learnerStore');
          useLearnerStore.getState().clearLearners();
        }
      },

      fetchUser: async () => {
        if (!localStorage.getItem('access_token')) return;
        set({ isLoading: true });
        try {
          const user = await authApi.getMe();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch {
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      setSession: async ({ access_token, refresh_token }) => {
        set({ isLoading: true });
        try {
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);

          const user = await authApi.getMe();
          set({ user, isAuthenticated: true, isLoading: false });

          const { useLearnerStore } = await import('./learnerStore');
          if (user?.id) {
            useLearnerStore.getState().setUserId(user.id);
          }
          // FP-LEARNER-STORE-RETIREMENT-01: useLearners() auto-fetches when
          // isAuthenticated flips true above — no imperative fetch here.
        } catch (err) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          set({ isLoading: false, error: 'Session could not be established' });
          throw err;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      // Packet A4: persist identity only. Entitlement (has_access, subscription,
      // access_reason) moves to useAccess() / ['access'] React Query.
      // onboarding_complete is derived server-side (A2) and read via useMe().
      // children was never consumed from persisted state. Key bump to v2 so
      // stale pre-A4 blobs don't resurrect obsolete fields on load.
      name: 'linguapals-auth-v2',
      partialize: (state) => ({
        user: state.user
          ? {
              id: state.user.id,
              email: state.user.email,
              name: state.user.name,
              timezone: state.user.timezone,
              locale: state.user.locale,
              is_admin: state.user.is_admin,
            }
          : null,
      }),
    }
  )
);
